import React, { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { LogsFormValues } from '@/entities/channel';
import { useLogsList } from '@/features/channels/model/useLogsList';
import { Log } from '@/shared/api/channels';
import { Box, Form, FormGenerator, MenuItem } from '@/shared/ui';
import { FormFieldValues } from '@/shared/ui/FormGenerator';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { SvgIcon } from '@/shared/ui/SvgIcon';
import { IDataRow, Table } from '@/shared/ui/Table';
import { dayjs, isValidDate } from '@/shared/utils/dayjs';

import { StyledFormBody } from './styles';

export const ChannelsLogsTab = ({ channelId }: { channelId?: string }) => {
  const [formValues, setFormValues] = useState<LogsFormValues>({
    from: null,
    to: null,
    severity: null,
  });

  const { t } = useTranslation();
  const { logs, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useLogsList(channelId ?? '', formValues);

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '50px',
  });

  // Загружаем следующую страницу при скролле
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const getStatusIcon = (severity: Log['severity']) => {
    switch (severity) {
      case 'info':
        return <SvgIcon name="warning" color="info" />;
      case 'warn':
        return <SvgIcon name="warning" color="warning" />;
      case 'error':
      case 'critical':
        return <SvgIcon name="warning" color="error" />;
      default:
        return <SvgIcon name="warning" color="info" />;
    }
  };

  const data: IDataRow[] = logs?.map((log) => ({
    key: log.id,
    columns: [
      {
        key: 'status',
        value: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getStatusIcon(log.severity)}
            {log.content}
          </div>
        ),
      },
      {
        key: 'date',
        value: dayjs(log.event_time).format('DD.MM.YYYY HH:mm'),
        align: 'right',
      },
    ],
  }));

  if (hasNextPage) {
    data.push({
      key: 'loading-row',
      columns: [
        {
          key: 'loading',
          value: (
            <div ref={ref} style={{ textAlign: 'center', padding: '16px' }}>
              <Loader containerSize="auto" />
            </div>
          ),
          colSpan: 2,
        },
      ],
    });
  }

  const handleFormSubmit = (val: FieldValues) => {
    setFormValues({
      from: isValidDate(val.from) ? val.from.utc().toISOString() : null,
      to: isValidDate(val.to) ? val.to.utc().toISOString() : null,
      severity: val.severity ? val.severity : null,
    });
  };

  const formScheme = [
    {
      type: FormFieldValues.Date,
      name: 'from',
      label: (
        <span style={{ color: 'white' }}>{t('channels:logs.inputs.from')}</span>
      ),
      inputProps: {
        disableFuture: true,
      },
    },
    {
      type: FormFieldValues.Date,
      name: 'to',
      label: (
        <span style={{ color: 'white' }}>{t('channels:logs.inputs.to')}</span>
      ),
      inputProps: {
        disableFuture: true,
        minDate: dayjs(formValues.from),
      },
    },
    {
      type: FormFieldValues.Select,
      name: 'severity',
      label: (
        <span style={{ color: 'white' }}>
          {t('channels:logs.inputs.severity')}
        </span>
      ),
      inputProps: {
        children: [
          <MenuItem value="" key="none" sx={{ height: 38 }} />,
          <MenuItem value="info" key="info">
            info
          </MenuItem>,
          <MenuItem value="warn" key="warn">
            warn
          </MenuItem>,
          <MenuItem value="ok" key="ok">
            ok
          </MenuItem>,
        ],
      },
    },
  ];

  return (
    <>
      <Form
        id="channels_logs_form"
        onSubmit={handleFormSubmit}
        onFieldChange={handleFormSubmit}
        defaultValues={{ from: null, to: null, severity: '' }}
      >
        <StyledFormBody>
          <FormGenerator formScheme={formScheme} />
        </StyledFormBody>
      </Form>
      {isPending ? (
        <Loader containerSize="medium" />
      ) : (
        <Box sx={{ mb: 2 }}>
          {logs?.length > 0 ? (
            <Table data={data} />
          ) : (
            <NotFoundData title={t('channels:logs.notFoundData.title')} />
          )}
        </Box>
      )}
    </>
  );
};
